import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/state/authStore";

interface AppHeaderProps {
  onOpenMobileMenu: () => void;
}

export function AppHeader({ onOpenMobileMenu }: AppHeaderProps) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Clique fora e Esc fecham o menu: sem isso ele fica preso aberto por cima da
  // tela e o usuário precisa clicar exatamente no mesmo botão para sair.
  useEffect(() => {
    if (!menuOpen) return undefined;
    function handlePointer(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const initials = (user?.name ?? "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <header className="flex h-14 items-center justify-between border-b border-ink-700 bg-ink-900 px-4 sm:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded border border-ink-700 bg-ink-800 p-2 text-ink-100 hover:bg-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 lg:hidden"
          aria-label="Abrir menu lateral"
          onClick={onOpenMobileMenu}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <Link to="/manager" className="flex items-center" aria-label="LinkFlow Viewer">
          {/* Mesma marca do viewer. O shell é sempre escuro, então usa direto o
              lockup em branco — a versão padrão é azul-escuro e sumiria aqui. */}
          <img
            src="/linkflow-logo-dark.png"
            alt="LinkFlow"
            className="h-7 w-auto object-contain"
            draggable={false}
          />
        </Link>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center gap-2 rounded border border-ink-700 bg-ink-800 px-2 py-1.5 text-left hover:bg-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-900/60 text-[11px] font-medium text-emerald-200"
              aria-hidden="true"
            >
              {initials || "?"}
            </span>
            <span className="hidden sm:block">
              <span className="block text-sm leading-tight text-ink-200">
                {user?.name ?? "Usuario autenticado"}
              </span>
              <span className="block text-xs leading-tight text-ink-400">
                {user?.email ?? "sem email"}
              </span>
            </span>
            <svg viewBox="0 0 24 24" className="h-3 w-3 text-ink-400" fill="none" aria-hidden="true">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-md border border-ink-700 bg-ink-850 shadow-xl"
            >
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-xs text-ink-100 hover:bg-ink-800"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/alterar-senha");
                }}
              >
                Alterar senha
              </button>
              <button
                type="button"
                role="menuitem"
                className="block w-full border-t border-ink-700 px-3 py-2 text-left text-xs text-ink-100 hover:bg-ink-800"
                onClick={() => {
                  setMenuOpen(false);
                  void handleLogout();
                }}
              >
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
